import React, { useCallback, useEffect, useState, Suspense } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { Grid } from '@material-ui/core'
import { useParams, navigate } from '@reach/router'
import { Button, Datagrid, Loader } from '@refera/ui-web'

import { useToast } from '_/hooks/use-toast'
import { formatErrorMessage } from '_/views/finance/utils/FormatErrorMessage'
import { getAgencyProviders, setAgencyProvidersTablePage } from '_modules/provider/actions'
import {
  agencyProvidersResultSelector,
  agencyProvidersSelector,
  getProvidersLoadingSelector,
} from '_modules/provider/selectors'

import HeaderTitle from '_/components/header-title'

import { TABLE_SX } from '_/views/profiles/constants'
import { ProvidersTableColumns } from './columns'
import humps from 'humps'

import useStyles from './styles'
import { PERMISSIONS, PERMISSIONS_ACTIONS } from '_/utils/constants/permissions'
import useRolePermission from '_/hooks/use-role-permission'
import { ROUTES } from '_/utils/constants'

const AddAgencyProviderModal = React.lazy(() => import('./crud-agency-provider-modal'))

const INITIAL_PAGE_SIZE = 100

const AgencyProviders = () => {
  const styles = useStyles()

  const dispatch = useDispatch()
  const { agencyId } = useParams()

  const results = useSelector(agencyProvidersResultSelector)
  const { count, loadedPages } = useSelector(agencyProvidersSelector)
  const providersIsLoading = useSelector(getProvidersLoadingSelector)

  const { checkUserPermission } = useRolePermission()

  const [page, setPage] = useState(0)
  const [orderBy, setOrderBy] = useState('')
  const [pageSize, setPageSize] = useState(INITIAL_PAGE_SIZE)
  const [editMode, setEditMode] = useState(false)
  const [providerToEdit, setProviderToEdit] = useState(null)
  const [openNewProviderModal, setOpenNewProviderModal] = useState(false)

  const { showToast } = useToast()

  const handleGetProviders = useCallback(() => {
    dispatch(getAgencyProviders({ agencyId, pageSize, page: page + 1, orderBy })).catch(error => {
      showToast({ type: 'error', message: formatErrorMessage(error) })
    })
    dispatch(setAgencyProvidersTablePage({ page }))
  }, [dispatch, loadedPages, agencyId, showToast, page, pageSize, orderBy])

  const handleOrderBy = useCallback(
    orderObj => {
      const order = orderObj?.[0] || ''
      if (!order) {
        setOrderBy('name')
        return
      }
      const { field, sort } = order
      const decamelizedField = humps.decamelize(field)
      sort === 'desc' ? setOrderBy(`-${decamelizedField}`) : setOrderBy(decamelizedField)
    },
    [setOrderBy]
  )

  const handleOpenNewProviderModal = () => {
    checkUserPermission({
      permission: PERMISSIONS.TRADESMAN,
      permissionAction: [PERMISSIONS_ACTIONS.EDIT, PERMISSIONS_ACTIONS.ADD],
      functionAccess: () => navigate(`${ROUTES.CREATE_PROVIDER}/?intermediaria=${agencyId}`),
    })
  }

  const handleCloseCRUDModal = useCallback(() => {
    setEditMode(false)
    setProviderToEdit(null)
    handleGetProviders()
  }, [handleGetProviders])

  const handleCellClick = useCallback(
    (params, event) => {
      event.stopPropagation()
      navigate(`${ROUTES.PROVIDER}/${params.row.id}`, {
        state: {
          fromAgencyProviderId: agencyId,
        },
      })
    },
    [agencyId]
  )

  const handleBackButton = useCallback(() => {
    navigate(`${ROUTES.AGENCY}/${agencyId}`)
  }, [agencyId])

  useEffect(() => {
    handleGetProviders()
  }, [page, pageSize, orderBy])

  return (
    <Grid container className={styles.container} style={{ position: 'relative' }}>
      <HeaderTitle title="Prestadores" backButtonAction={handleBackButton}>
        <div className={styles.actionButtons}>
          <Button onClick={handleOpenNewProviderModal} disabled={providersIsLoading}>
            Novo
          </Button>
        </div>
      </HeaderTitle>
      <Grid className={styles.tableContainer}>
        <Grid className={styles.mainTable}>
          <Datagrid
            page={page}
            sx={TABLE_SX}
            rowCount={count}
            pageSize={pageSize}
            onPageChange={setPage}
            paginationMode="server"
            loading={providersIsLoading}
            onPageSizeChange={setPageSize}
            onSortModelChange={handleOrderBy}
            columns={ProvidersTableColumns()}
            rows={results?.length ? results : []}
            onCellClick={handleCellClick}
          />
        </Grid>
      </Grid>
      <Suspense fallback={<Loader open />}>
        <AddAgencyProviderModal
          open={openNewProviderModal}
          setOpen={setOpenNewProviderModal}
          onClose={handleCloseCRUDModal}
          agencyId={agencyId}
          editMode={editMode}
          providerToEdit={providerToEdit}
        />
      </Suspense>
    </Grid>
  )
}

export default AgencyProviders
